import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/lib/i18n";
import { Toaster } from "sonner";
import "@/App.css";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Sessions from "@/pages/Sessions";
import AppShell from "@/components/AppShell";
import StaffDashboard from "@/pages/staff/StaffDashboard";
import FindJobs from "@/pages/staff/FindJobs";
import JobDetail from "@/pages/staff/JobDetail";
import MyBookings from "@/pages/staff/MyBookings";
import Availability from "@/pages/staff/Availability";
import Documents from "@/pages/staff/Documents";
import StaffProfile from "@/pages/staff/StaffProfile";
import Notifications from "@/pages/Notifications";
import CompanyDashboard from "@/pages/company/CompanyDashboard";
import CreateJob from "@/pages/company/CreateJob";
import CompanyJobs from "@/pages/company/CompanyJobs";
import CompanyBookings from "@/pages/company/CompanyBookings";
import CompanyProfile from "@/pages/company/CompanyProfile";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminJobs from "@/pages/admin/AdminJobs";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminDocuments from "@/pages/admin/AdminDocuments";
import Messages from "@/pages/Messages";
import PaymentReturn from "@/pages/PaymentReturn";
import Support from "@/pages/Support";
import Payouts from "@/pages/staff/Payouts";
import Referrals from "@/pages/Referrals";
import AdminLive from "@/pages/admin/AdminLive";
import Invoices from "@/pages/company/Invoices";
import Team from "@/pages/company/Team";
import BulkJobs from "@/pages/company/BulkJobs";
import SkillTests from "@/pages/staff/SkillTests";
import PublicJob from "@/pages/PublicJob";
import Swaps from "@/pages/staff/Swaps";
import Favourites from "@/pages/company/Favourites";
import AdminRateLimits from "@/pages/admin/AdminRateLimits";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminDispatch from "@/pages/admin/AdminDispatch";
import AdminMatching from "@/pages/admin/AdminMatching";
import Offers from "@/pages/staff/Offers";

function Protected({ role, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 label-caps">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to={`/${user.role}`} replace />;
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 label-caps">Loading…</div>;
  if (!user) return <Landing />;
  return <Navigate to={`/${user.role}`} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <I18nProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/j/:id" element={<PublicJob />} />

          <Route path="/staff" element={<Protected role="staff"><AppShell /></Protected>}>
            <Route index element={<StaffDashboard />} />
            <Route path="jobs" element={<FindJobs />} />
            <Route path="jobs/:id" element={<JobDetail />} />
            <Route path="bookings" element={<MyBookings />} />
            <Route path="availability" element={<Availability />} />
            <Route path="documents" element={<Documents />} />
            <Route path="profile" element={<StaffProfile />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="messages" element={<Messages />} />
            <Route path="support" element={<Support />} />
            <Route path="payouts" element={<Payouts />} />
            <Route path="referrals" element={<Referrals />} />
            <Route path="skill-tests" element={<SkillTests />} />
            <Route path="swaps" element={<Swaps />} />
            <Route path="offers" element={<Offers />} />
            <Route path="sessions" element={<Sessions />} />
          </Route>

          <Route path="/company" element={<Protected role="company"><AppShell /></Protected>}>
            <Route index element={<CompanyDashboard />} />
            <Route path="jobs" element={<CompanyJobs />} />
            <Route path="jobs/new" element={<CreateJob />} />
            <Route path="jobs/bulk" element={<BulkJobs />} />
            <Route path="bookings" element={<CompanyBookings />} />
            <Route path="profile" element={<CompanyProfile />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="messages" element={<Messages />} />
            <Route path="support" element={<Support />} />
            <Route path="payment/return" element={<PaymentReturn />} />
            <Route path="referrals" element={<Referrals />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="team" element={<Team />} />
            <Route path="favourites" element={<Favourites />} />
            <Route path="sessions" element={<Sessions />} />
          </Route>

          <Route path="/admin" element={<Protected role="admin"><AppShell /></Protected>}>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="jobs" element={<AdminJobs />} />
            <Route path="bookings" element={<AdminBookings />} />
            <Route path="documents" element={<AdminDocuments />} />
            <Route path="support" element={<Support />} />
            <Route path="live" element={<AdminLive />} />
            <Route path="rate-limits" element={<AdminRateLimits />} />
            <Route path="audit" element={<AdminAudit />} />
            <Route path="dispatch" element={<AdminDispatch />} />
            <Route path="matching" element={<AdminMatching />} />
            <Route path="sessions" element={<Sessions />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </I18nProvider>
    </AuthProvider>
  );
}
